import { call, put, takeEvery, all, fork } from "redux-saga/effects";
import { api } from "../services/apiService";
import type from "../actions/types";
import { isApiFailure, isUpdateApiFailure, apiFailureStatus } from "./apiFailure";

// Every saga here dispatched SUCCESS UNCONDITIONALLY on resolve — but the
// fetchers never reject (a failure resolves {status}/{errStatus}), so the
// catch/ERROR branch was dead code and every failure paraded as SUCCESS
// (#603 gate finding 6). Each now classifies the resolve; the ERROR action's
// reducer maps `status` → an error message (reducers/payment.js).
// Predicate per fetcher family (#612 gate): doGetCall-backed → isApiFailure;
// doUpdateCall-backed → isUpdateApiFailure (errStatus ONLY — a real Booking
// success body carries a domain top-level status like "SUCCESS"/"CANCELED",
// which must never classify as failure; the post-checkout path rides here).

function* getActivePlans(action) {
    try {
        const activePlansRes = yield call(api.getActivePlans, action.payload);
        if (isApiFailure(activePlansRes)) {
            yield put({ type: type.GET_ACTIVE_PLANS_ERROR, status: apiFailureStatus(activePlansRes) });
        } else {
            yield put({ type: type.GET_ACTIVE_PLANS_SUCCESS, plansList: activePlansRes });
        }
    } catch (e) {
        yield put({ type: type.GET_ACTIVE_PLANS_ERROR, message: e.message });
    }
}

function* getUserPackage(action) {
    try {
        const packageRes = yield call(api.getUserPackage, action.payload);
        if (isApiFailure(packageRes)) {
            yield put({ type: type.GET_USER_PACKAGE_ERROR, status: apiFailureStatus(packageRes) });
        } else {
            yield put({ type: type.GET_USER_PACKAGE_SUCCESS, userPackage: packageRes });
        }
    } catch (e) {
        yield put({ type: type.GET_USER_PACKAGE_ERROR, message: e.message });
    }
}

function* createPaymentSession(action) {
    try {
        const paymentSessionRes = yield call(api.createPaymentSession, action.payload);
        if (isUpdateApiFailure(paymentSessionRes)) {
            yield put({ type: type.GET_PAYMENT_SESSION_ERROR, status: apiFailureStatus(paymentSessionRes) });
        } else {
            yield put({ type: type.GET_PAYMENT_SESSION_SUCCESS, paymentSession: paymentSessionRes });
        }
    } catch (e) {
        yield put({ type: type.GET_PAYMENT_SESSION_ERROR, message: e.message });
    }
}

function* updatePaymentStatus(action) {
    try {
        const paymentUpdateRes = yield call(api.updatePaymentStatus, action.payload);
        // Booking.status is "SUCCESS"/"FAILED" on genuine success — errStatus only.
        if (isUpdateApiFailure(paymentUpdateRes)) {
            yield put({ type: type.GET_PAYMENT_STATUS_ERROR, status: apiFailureStatus(paymentUpdateRes) });
        } else {
            yield put({ type: type.GET_PAYMENT_STATUS_SUCCESS, paymentUpdate: paymentUpdateRes });
        }
    } catch (e) {
        yield put({ type: type.GET_PAYMENT_STATUS_ERROR, message: e.message });
    }
}
function* getUserPayments(action) {
    try {
        const paymentHistRes = yield call(api.getUserPayments, action.payload);
        if (isApiFailure(paymentHistRes)) {
            yield put({ type: type.GET_USER_PAYMENT_ERROR, status: apiFailureStatus(paymentHistRes) });
        } else {
            yield put({ type: type.GET_USER_PAYMENT_SUCCESS, paymentHistory: paymentHistRes });
        }
    } catch (e) {
        yield put({ type: type.GET_USER_PAYMENT_ERROR, message: e.message });
    }
}
function* cancelSubscription(action) {
    try {
        const cancelSubscriptionRes = yield call(api.cancelSubscription, action.payload);
        // Booking.status is "CANCELED"/"CANCELLATION_IN_PROGRESS" on genuine success.
        if (isUpdateApiFailure(cancelSubscriptionRes)) {
            yield put({ type: type.CANCEL_SUBSCRIPTION_ERROR, status: apiFailureStatus(cancelSubscriptionRes) });
        } else {
            yield put({ type: type.CANCEL_SUBSCRIPTION_SUCCESS, cancelSubscription: cancelSubscriptionRes });
        }
    } catch (e) {
        yield put({ type: type.CANCEL_SUBSCRIPTION_ERROR, message: e.message });
    }
}
function* selectPlan(action) {
    try {
        const selectPlanRes = yield call(api.selectPlan, action.payload);
        if (isUpdateApiFailure(selectPlanRes)) {
            yield put({ type: type.SELECT_PLAN_ERROR, status: apiFailureStatus(selectPlanRes) });
        } else {
            yield put({ type: type.SELECT_PLAN_SUCCESS, selectPlan: selectPlanRes });
        }
    } catch (e) {
        yield put({ type: type.SELECT_PLAN_ERROR, message: e.message });
    }
}

// Test seam: failure classification for every payment saga (api never rejects).
export const testable = {
    getActivePlans,
    getUserPackage,
    createPaymentSession,
    updatePaymentStatus,
    getUserPayments,
    cancelSubscription,
    selectPlan,
};

function* watchGetActivePlans() {
    yield takeEvery(type.GET_ACTIVE_PLANS_REQUEST, getActivePlans);
}

function* watchCreatePaymentSession() {
    yield takeEvery(type.GET_PAYMENT_SESSION_REQUEST, createPaymentSession);
}

function* watchUpdatePaymentStatus() {
    yield takeEvery(type.GET_PAYMENT_STATUS_REQUEST, updatePaymentStatus);
}
function* watchGetUserPackage() {
    yield takeEvery(type.GET_USER_PACKAGE_REQUEST, getUserPackage);
}
function* watchGetUserPayments() {
    yield takeEvery(type.GET_USER_PAYMENT_REQUEST, getUserPayments);
}
function* watchCancelSubscription() {
    yield takeEvery(type.CANCEL_SUBSCRIPTION_REQUEST, cancelSubscription);
}
function* watchSelectPlan() {
    yield takeEvery(type.SELECT_PLAN_REQUEST, selectPlan);
}

export default function* paymentSaga() {
    yield all([
        fork(watchGetActivePlans),
        fork(watchCreatePaymentSession),
        fork(watchUpdatePaymentStatus),
        fork(watchGetUserPackage),
        fork(watchGetUserPayments),
        fork(watchCancelSubscription),
        fork(watchSelectPlan),
    ]);
}